import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { PageMeta } from '@/lib/types'

export type UserStatus = 'active' | 'suspended' | 'deleted'

export interface AdminUser {
  id: string
  email: string
  full_name: string
  phone?: string
  status: UserStatus
  roles: string[]
  email_verified: boolean
  avatar_url?: string
  created_at: string
}

interface UserList {
  users: AdminUser[]
  meta: PageMeta
}

export interface UserFilters {
  q?: string
  status?: string
  role?: string
  page?: number
}

export function useAdminUsers(filters: UserFilters) {
  return useQuery({
    queryKey: ['admin-users', filters],
    queryFn: () => api.get<UserList>('/admin/users', { query: { ...filters, per_page: 20 } }),
  })
}

export interface CreateUserInput {
  email: string
  full_name: string
  phone?: string
  password?: string
  roles: string[]
}

export interface UpdateUserInput {
  full_name?: string
  phone?: string
  status?: UserStatus
  roles?: string[]
}

/** Tanggapan yang memuat kata sandi hanya datang sekali dan tidak disimpan. */
interface UserWithPassword {
  user: AdminUser
  generated_password?: string
  sessions_ended?: boolean
}

function useUserMutation<TInput, TData>(mutationFn: (input: TInput) => Promise<TData>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => client.invalidateQueries({ queryKey: ['admin-users'] }),
  })
}

export function useCreateUser() {
  return useUserMutation((input: CreateUserInput) => api.post<UserWithPassword>('/admin/users', input))
}

export function useUpdateUser() {
  return useUserMutation(({ id, ...input }: UpdateUserInput & { id: string }) =>
    api.patch<{ user: AdminUser }>(`/admin/users/${id}`, input),
  )
}

export function useDeleteUser() {
  return useUserMutation((id: string) => api.delete<void>(`/admin/users/${id}`))
}

export function useResetUserPassword() {
  return useUserMutation(({ id, password }: { id: string; password?: string }) =>
    api.post<UserWithPassword>(`/admin/users/${id}/password`, { password: password || undefined }),
  )
}
